/**
 * `ProofStatusPanel` — ADR-0043 replacement for the DNS-CNAME-based
 * `VerifyDomainPanel`. ADR-0050 §1.2 trims the panel down: the install
 * snippet block + Copy button + "PASTE THIS SNIPPET" header are removed
 * (the merchant sees the snippet exactly once now, in
 * `ScriptTagInstallPanel`). What remains:
 *
 *   1. **Loading / load-error states for the `proof-token` payload** —
 *      drives the `.well-known` fallback body + the snippet's
 *      `data-proof-token` attribute over in `ScriptTagInstallPanel`
 *      (via the shared `useProofCheck` state).
 *
 *   2. **Fallback (.well-known) affordance** — surfaces only when the
 *      homepage scan returns `homepage_miss` /
 *      `homepage_miss_and_well_known_mismatch`. Body + URL copy
 *      preserved verbatim.
 *
 *   3. **OAuth-as-proof read-only summary** — if Shopify or Stripe is
 *      connected, the callback's `domain_match` evaluation populates a
 *      P2 row; the `proof_status` payload from
 *      `GET /v1/sites/:site_id` surfaces it here. (Slice 1 ships
 *      read-only; the reconciliation flow is a follow-up.)
 *
 * The Verify button itself is rendered inline in `ScriptTagInstallPanel`
 * (paste-once / verify-once). When this panel is used without an
 * external `proofCheck` prop (legacy callers) it still falls back to
 * its own hook AND renders the Verify control here so behavior is
 * preserved.
 *
 * Wire contract unchanged: same `GET /v1/sites/:site_id/proof-token`,
 * same `POST /v1/sites/:site_id/proof-check`, same `safe-fetch` origin
 * posture, same `pending_proof` audit-write gate.
 */

import { motion } from 'framer-motion';
import { ArrowRight, Check, Copy, RefreshCw, ShoppingBag } from 'lucide-react';
import { useState } from 'react';

import type { SiteId } from '@sill/types';

import { API_ORIGIN } from '../../app/env.js';
import { useC } from '../../lib/theme.js';
import { VERIFY_HINT } from './install-copy.js';
import { Card } from '../../components/atoms/index.js';
import {
  useProofCheck,
  type UseProofCheckState,
} from '../../hooks/useProofCheck.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

/**
 * Build the Shopify OAuth install URL — mirrors the construction
 * `ShopifyConnectPanel.shopifyInstallUrl` uses so the password-page
 * friendly-miss CTA initiates the same OAuth-as-proof flow. ADR-0052
 * §2.1 / §4.
 */
function buildShopifyInstallUrl(site_id: SiteId, shop: string): string {
  const u = new URL(`${API_ORIGIN}/v1/oauth/shopify/install`);
  u.searchParams.set('site_id', site_id);
  u.searchParams.set('shop', shop);
  return u.toString();
}

export interface ProofStatusPanelProps {
  /** Fires on a successful proof-check. The composing step can advance. */
  onVerified?: () => void;
  /** Fires when the merchant defers the verify step. */
  onSkip?: () => void;
  /** API base URL — usually pulled from app config. Forwarded to the
   *  internal `useProofCheck` hook when `proofCheck` is not provided. */
  api_base_url?: string;
  /** Optional shared proof-check state from a parent-mounted
   *  `useProofCheck` instance. When provided (ADR-0050 §1.2 — the
   *  `InstallVerifyStep` case), this panel does NOT mount its own
   *  hook and the inline Verify button is NOT rendered (the snippet's
   *  Verify control over in `ScriptTagInstallPanel` is authoritative).
   *  When omitted (legacy callers), the panel mounts its own hook and
   *  renders the Verify control here. */
  proofCheck?: UseProofCheckState;
}

export function ProofStatusPanel({
  onVerified,
  onSkip,
  api_base_url,
  proofCheck: externalProofCheck,
}: ProofStatusPanelProps): JSX.Element {
  const C = useC();
  // Mount an internal hook ONLY when no external state was injected.
  // Both branches must call the hook unconditionally to satisfy
  // React's rules-of-hooks contract; the `enabled: false` short-
  // circuit prevents a duplicate `GET /proof-token` fetch when the
  // parent (`InstallVerifyStep`) already owns the canonical hook
  // instance in the post-ADR-0050 flow.
  const internalEnabled = externalProofCheck === undefined;
  const internalProofCheck = useProofCheck({
    enabled: internalEnabled,
    ...(api_base_url !== undefined ? { apiBaseUrl: api_base_url } : {}),
    ...(onVerified !== undefined ? { onVerified } : {}),
  });
  const proof = externalProofCheck ?? internalProofCheck;
  const renderVerifyHere = externalProofCheck === undefined;
  const [copied_token, setCopiedToken] = useState(false);

  // Injected-state path with nothing to say: no load problem, not
  // verified, no failure CTA, no fallback. 2026-10-21: `checking` is no
  // longer rendered here either — the inline Verify button owns that
  // spinner and this panel must not render a second concurrent one.
  // Rendering a card whose only content was an idle "WAITING FOR VERIFY"
  // kicker was noise (founder 2026-08-04).
  const externalIdle =
    !renderVerifyHere
    && !proof.loading
    && proof.data !== null
    && !proof.verified
    && proof.lastFailure?.show_oauth_cta !== true
    && !proof.showFallback;
  if (externalIdle) {
    return <></>;
  }

  const copy_token = (): void => {
    if (proof.data === null) return;
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(proof.data.fallback.well_known_body);
    }
    setCopiedToken(true);
    setTimeout(() => setCopiedToken(false), 1800);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="p-5">
        {/* Kicker + rationale only in the legacy self-hosted verify path.
            In the default injected-state onboarding path the Verify control
            lives with the snippet above, so this panel collapses to just
            the status row below — no header, no essay (founder 2026-07-29). */}
        {renderVerifyHere ? (
          <>
            <div
              className="flex items-center gap-2 flex-wrap"
              style={{
                fontFamily: monoFont,
                fontSize: 10,
                color: C.muted,
                letterSpacing: 2,
                marginBottom: 12,
              }}
            >
              PROVE DOMAIN OWNERSHIP
            </div>
            <p
              style={{
                fontFamily: sansFont,
                fontSize: 14,
                color: C.text,
                lineHeight: 1.55,
                marginBottom: 14,
                maxWidth: 640,
              }}
            >
              {VERIFY_HINT}
            </p>
          </>
        ) : null}

        {proof.loading ? (
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.muted,
              padding: '12px 0',
            }}
          >
            Loading proof token…
          </div>
        ) : proof.data === null ? (
          <div
            className="flex flex-col sm:flex-row sm:items-center gap-3"
            style={{ padding: '12px 0' }}
          >
            <span
              style={{
                fontFamily: sansFont,
                fontSize: 13,
                color: C.coralFg,
                lineHeight: 1.5,
              }}
            >
              Couldn't load the proof token
              {proof.loadError !== null ? ` (${proof.loadError})` : ''}.
            </span>
            <button
              type="button"
              onClick={() => proof.reload()}
              className="flex items-center justify-center gap-1.5 px-3 py-1.5"
              style={{
                background: 'transparent',
                border: `1px solid ${C.border}`,
                color: C.text,
                fontFamily: sansFont,
                fontSize: 13,
                borderRadius: 2,
                alignSelf: 'flex-start',
              }}
            >
              <RefreshCw size={13} /> Retry
            </button>
          </div>
        ) : (
          <>
            {/* Verify control — rendered here ONLY when no external
                proofCheck was injected (legacy callers). The
                ADR-0050 InstallVerifyStep injects the state and the
                inline Verify in ScriptTagInstallPanel is authoritative. */}
            {renderVerifyHere ? (
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                {/* ADR-0049 — verify is mandatory; the "Verify later"
                    Skip affordance only renders when `onSkip` is
                    explicitly provided. `InstallVerifyStep` does NOT
                    pass it, so the button never appears in the new
                    onboarding flow. The prop stays on the public
                    interface for backward compatibility with callers
                    that still want a Skip path. */}
                {onSkip !== undefined ? (
                  <button
                    type="button"
                    onClick={onSkip}
                    className="px-4 py-2.5"
                    style={{
                      background: 'transparent',
                      border: `1px solid ${C.border}`,
                      color: C.muted,
                      fontFamily: sansFont,
                      fontSize: 13,
                      borderRadius: 2,
                    }}
                  >
                    Verify later
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => void proof.verify()}
                  disabled={proof.checking || proof.site === null || proof.verified}
                  className="flex items-center justify-center gap-2 px-5 py-2.5"
                  style={{
                    background: proof.verified ? C.limeDim : C.lime,
                    color: proof.verified ? C.limeFg : C.ink,
                    fontFamily: sansFont,
                    fontWeight: 600,
                    fontSize: 14,
                    borderRadius: 2,
                    opacity: proof.checking || proof.site === null ? 0.6 : 1,
                    border: proof.verified ? `1px solid ${C.limeFg}` : 'none',
                  }}
                >
                  {proof.checking ? (
                    <>
                      <RefreshCw
                        size={15}
                        style={{ animation: 'spin 1.4s linear infinite' }}
                      />
                      Checking…
                    </>
                  ) : proof.verified ? (
                    'Domain verified'
                  ) : (
                    <>
                      Verify <ArrowRight size={15} />
                    </>
                  )}
                </button>
              </div>
            ) : null}

            {/* Status line — surfaced in BOTH the renderVerifyHere and
                external-hook flows so the merchant always knows the
                current proof state. The inline Verify button owns the
                "Checking…" spinner (ADR-0050 §1.2); this panel never
                renders a second spinner for the same check (2026-10-21
                bug fix: two concurrent "Checking…" lines). */}
            {!renderVerifyHere && proof.verified ? (
              <div
                className="flex items-center gap-2"
                style={{
                  fontFamily: monoFont,
                  fontSize: 11,
                  color: C.limeFg,
                  letterSpacing: 1.5,
                  padding: '4px 0',
                }}
                data-testid="proof-status-line"
              >
                <Check size={12} /> DOMAIN VERIFIED
              </div>
            ) : null}

            {/* ADR-0052 §2 — Shopify-password-page friendly miss. When
                the most recent verify failed AND the friendly mapping
                set `show_oauth_cta` (the detected-Shopify homepage-miss
                branch in `friendlyVerifyMessage`), surface the
                "Store is password-protected" copy + the inline Connect
                Shopify CTA. This is a UX label on an ALREADY-failed
                match; the proof primitive is unchanged. The CTA links
                to the same `/v1/oauth/shopify/install` URL the
                ShopifyConnectPanel uses. */}
            {proof.lastFailure?.show_oauth_cta === true
              && proof.site !== null ? (
              <div
                className="mt-5 p-4"
                style={{
                  background: C.surface,
                  border: `1px solid ${C.amberFg}`,
                  borderRadius: 2,
                }}
                data-testid="proof-password-page-cta"
              >
                <div
                  style={{
                    fontFamily: monoFont,
                    fontSize: 10,
                    color: C.amberFg,
                    letterSpacing: 2,
                    marginBottom: 10,
                  }}
                >
                  STORE IS PASSWORD-PROTECTED
                </div>
                <p
                  style={{
                    fontFamily: sansFont,
                    fontSize: 13,
                    color: C.text,
                    lineHeight: 1.55,
                    marginBottom: 12,
                  }}
                >
                  {proof.lastFailure.body}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    if (proof.site === null) return;
                    const url = buildShopifyInstallUrl(
                      proof.site.site_id,
                      proof.site.domain,
                    );
                    window.location.assign(url);
                  }}
                  className="flex items-center gap-2 px-4 py-2"
                  style={{
                    background: C.lime,
                    color: C.ink,
                    fontFamily: sansFont,
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: 2,
                    border: 'none',
                  }}
                  aria-label="Connect Shopify"
                >
                  <ShoppingBag size={13} /> Connect Shopify
                </button>
              </div>
            ) : null}

            {proof.showFallback && (
              <div
                className="mt-5 p-4"
                style={{
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: 2,
                }}
              >
                <div
                  style={{
                    fontFamily: monoFont,
                    fontSize: 10,
                    color: C.muted,
                    letterSpacing: 2,
                    marginBottom: 10,
                  }}
                >
                  OPTIONAL FALLBACK · ONLY IF THE SNIPPET CAN'T BE FOUND
                </div>
                <p
                  style={{
                    fontFamily: sansFont,
                    fontSize: 13,
                    color: C.text,
                    lineHeight: 1.55,
                    marginBottom: 10,
                  }}
                >
                  If your site is fully client-rendered (the snippet
                  loads via JS after the page does), publish the token
                  at this URL and click Verify again.
                </p>
                <div
                  className="mb-2"
                  style={{
                    fontFamily: monoFont,
                    fontSize: 12,
                    color: C.text,
                  }}
                >
                  <strong>URL:</strong> {proof.data.fallback.well_known_url}
                </div>
                <div
                  className="flex items-center gap-2"
                  style={{
                    fontFamily: monoFont,
                    fontSize: 12,
                    color: C.text,
                  }}
                >
                  <strong>Body (single line):</strong>{' '}
                  <code
                    style={{
                      wordBreak: 'break-all',
                      background: C.surface2,
                      padding: '2px 6px',
                      borderRadius: 2,
                    }}
                  >
                    {proof.data.fallback.well_known_body}
                  </code>
                  <button
                    type="button"
                    onClick={copy_token}
                    className="flex items-center gap-1 px-2 py-1"
                    style={{
                      fontFamily: sansFont,
                      fontSize: 11,
                      color: C.muted,
                      background: 'transparent',
                      border: `1px solid ${C.border}`,
                      borderRadius: 2,
                    }}
                  >
                    {copied_token ? <Check size={11} /> : <Copy size={11} />}
                    {copied_token ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </motion.div>
  );
}
